import { useState } from "react";
import { Link, useLocation } from "wouter";
import { motion } from "framer-motion";
import {
  Shield, Lock, Eye, Key, CheckCircle2, EyeOff, Plug, ArrowRight,
  User, Mail, LogOut, Loader2, CreditCard, Sparkles, ExternalLink,
  Download, Trash2, AlertTriangle, FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/store";
import { useToast } from "@/hooks/use-toast";
import { apiFetch } from "@/lib/api-fetch";
import { VoicePreferencesSection } from "@/components/voice-preferences-section";
import { IntegrationsContent } from "@/pages/integrations";

const SECURITY_BADGES = [
  { icon: Lock,   label: "256-bit Encryption",  desc: "Your data is encrypted at rest and in transit" },
  { icon: Shield, label: "Secure Auth",           desc: "Passwords are hashed with bcrypt" },
  { icon: Eye,    label: "Minimal Data",          desc: "We only collect what we need" },
  { icon: Key,    label: "Full Control",           desc: "Delete your account or data anytime" },
];

function AccountSection() {
  const { hasAccount, userEmail, isEmailVerified, authToken, logoutUser } = useAppStore();
  const { toast } = useToast();
  const [resending, setResending] = useState(false);

  if (!hasAccount) return null;

  const handleResend = async () => {
    setResending(true);
    try {
      const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");
      const res = await apiFetch(`${baseUrl}/api/auth/resend-verification`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast({ title: data.error || "Failed to resend", variant: "destructive" });
        return;
      }
      toast({ title: "Verification email sent!", description: "Check your inbox." });
    } catch {
      toast({ title: "Failed to resend", variant: "destructive" });
    } finally {
      setResending(false);
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <User className="w-5 h-5 text-primary" />
        <h2 className="text-xl font-display font-bold">Your Account</h2>
      </div>
      <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-primary-foreground font-bold text-sm shrink-0">
            <Mail className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">{userEmail}</p>
            <div className="flex items-center gap-2 mt-0.5">
              {isEmailVerified ? (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-400 font-medium">
                  <CheckCircle2 className="w-3 h-3" /> Verified
                </span>
              ) : (
                <span className="text-xs text-amber-400 font-medium">Unverified</span>
              )}
            </div>
          </div>
        </div>

        {!isEmailVerified && (
          <div className="flex items-center gap-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
            <Mail className="w-4 h-4 text-amber-400 shrink-0" />
            <p className="text-sm text-amber-200/80 flex-1">
              Check your inbox to verify your email.
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResend}
              disabled={resending}
              className="text-amber-300 hover:text-amber-100 text-xs shrink-0"
            >
              {resending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Resend"}
            </Button>
          </div>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={logoutUser}
          className="rounded-xl"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Log out
        </Button>
      </div>
    </section>
  );
}

function BillingSection() {
  const { isSubscribed, subscriptionStatus, subscriptionPlan, subscriptionPeriodEnd, authToken, hasAccount } = useAppStore();
  const { toast } = useToast();
  const [portalLoading, setPortalLoading] = useState(false);

  if (!hasAccount) return null;

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");
      const res = await apiFetch(`${baseUrl}/api/payments/portal`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast({ title: data.error || "Failed to open billing portal", variant: "destructive" });
        return;
      }
      const { portalUrl } = await res.json();
      if (portalUrl) {
        window.open(portalUrl, "_blank");
      }
    } catch {
      toast({ title: "Something went wrong", variant: "destructive" });
    } finally {
      setPortalLoading(false);
    }
  };

  const handleUpgrade = async () => {
    setPortalLoading(true);
    try {
      const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");
      const res = await apiFetch(`${baseUrl}/api/payments/create-checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ plan: "yearly" }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast({ title: data.error || "Failed to start checkout", variant: "destructive" });
        return;
      }
      const { checkoutUrl } = await res.json();
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
    } catch {
      toast({ title: "Something went wrong", variant: "destructive" });
    } finally {
      setPortalLoading(false);
    }
  };

  const planLabel = subscriptionPlan === "yearly" ? "Pro Yearly" : subscriptionPlan === "monthly" ? "Pro Monthly" : "Free Plan";
  const renewalDate = subscriptionPeriodEnd
    ? new Date(subscriptionPeriodEnd).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : null;

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <CreditCard className="w-5 h-5 text-primary" />
        <h2 className="text-xl font-display font-bold">Your Plan</h2>
      </div>
      <div className="bg-card border border-border rounded-2xl p-6">
        {isSubscribed ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))" }}>
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">{planLabel}</p>
                {renewalDate && (
                  <p className="text-xs text-muted-foreground">
                    {subscriptionStatus === "canceled" ? "Access until" : "Renews"} {renewalDate}
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleManageBilling}
              disabled={portalLoading}
              className="rounded-xl"
            >
              {portalLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ExternalLink className="w-4 h-4 mr-2" />}
              Manage Billing
            </Button>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <p className="text-sm font-semibold text-foreground">Free Plan</p>
              <p className="text-sm text-muted-foreground mt-1">
                Upgrade to Pro for unlimited post generation across all platforms.
              </p>
            </div>
            <Button
              onClick={handleUpgrade}
              disabled={portalLoading}
              size="sm"
              className="rounded-xl whitespace-nowrap"
              style={{ background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))" }}
            >
              {portalLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Upgrade to Pro
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}

function DataManagementSection() {
  const { hasAccount, authToken, logoutUser } = useAppStore();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!hasAccount) return null;

  const handleExport = async () => {
    setExporting(true);
    try {
      const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");
      const res = await apiFetch(`${baseUrl}/api/auth/export`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast({ title: data.error || "Failed to export data", variant: "destructive" });
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "scribpost-data.json";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: "Data exported successfully" });
    } catch {
      toast({ title: "Failed to export data", variant: "destructive" });
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");
      const res = await apiFetch(`${baseUrl}/api/auth/account`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast({ title: data.error || "Failed to delete account", variant: "destructive" });
        return;
      }
      toast({ title: "Account deleted", description: "All your data has been permanently removed." });
      logoutUser();
      navigate("/login");
    } catch {
      toast({ title: "Failed to delete account", variant: "destructive" });
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <FileText className="w-5 h-5 text-primary" />
        <h2 className="text-xl font-display font-bold">Your Data</h2>
      </div>
      <div className="bg-card border border-border rounded-2xl p-6 space-y-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            <p className="text-sm font-semibold text-foreground">Download My Data</p>
            <p className="text-sm text-muted-foreground mt-0.5">
              Export all your data as a JSON file — posts, ideas, voice samples, brainstorms, and replies.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exporting}
            className="rounded-xl whitespace-nowrap"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
            Download
          </Button>
        </div>

        <div className="border-t border-border" />

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            <p className="text-sm font-semibold text-destructive">Delete My Account</p>
            <p className="text-sm text-muted-foreground mt-0.5">
              Permanently delete your account and all associated data. This cannot be undone.
            </p>
          </div>
          {!confirmDelete ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmDelete(true)}
              className="rounded-xl whitespace-nowrap border-destructive/30 text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Account
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDelete}
                disabled={deleting}
                className="rounded-xl whitespace-nowrap"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
                Yes, Delete Everything
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                className="rounded-xl"
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default function Settings() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto space-y-10"
    >
      <div>
        <h1 className="text-3xl font-display font-bold">Settings</h1>
        <p className="text-muted-foreground mt-2">
          Account, privacy preferences, and data controls.
        </p>
      </div>

      <AccountSection />
      <BillingSection />
      <VoicePreferencesSection />
      <DataManagementSection />

      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-emerald-400" />
          <h2 className="text-xl font-display font-bold">How we protect your data</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {SECURITY_BADGES.map((badge) => (
            <div
              key={badge.label}
              className="flex flex-col gap-2 p-4 bg-card border border-border rounded-2xl"
            >
              <div className="w-9 h-9 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <badge.icon className="w-4.5 h-4.5 text-emerald-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">{badge.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{badge.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <Plug className="w-5 h-5 text-primary" />
          <h2 className="text-xl font-display font-bold">Integrations</h2>
        </div>
        <IntegrationsContent />
      </section>

      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <EyeOff className="w-5 h-5 text-primary" />
          <h2 className="text-xl font-display font-bold">Privacy & Data</h2>
        </div>
        <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
          <div className="flex items-start gap-3 pb-4 border-b border-border">
            <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-foreground">Audio & Video Files</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                Your recordings and uploads are transcribed then immediately deleted. We never store the raw media — only the extracted text that trains your voice profile.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 pb-4 border-b border-border">
            <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-foreground">Generated Posts</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                Only the final text of your generated posts is saved to your account. Raw ideas and intermediate drafts are not persisted.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-foreground">Account Security</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                Your password is securely hashed and never stored in plain text. Email is only used for verification and account recovery.
              </p>
            </div>
          </div>
        </div>
      </section>

      <footer className="pt-4 pb-8 border-t border-border flex items-center justify-center gap-4 text-xs text-muted-foreground">
        <Link href="/privacy">
          <span className="hover:text-foreground transition-colors cursor-pointer">Privacy Policy</span>
        </Link>
        <span>&middot;</span>
        <Link href="/terms">
          <span className="hover:text-foreground transition-colors cursor-pointer">Terms of Service</span>
        </Link>
      </footer>
    </motion.div>
  );
}
